import { GluestackUIProvider } from '@gluestack-ui/themed';
import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useAppSelector } from '@/app/store';
import { selectIsAuthenticated, selectIsRestoringSession } from '@/features/auth/store/authSlice';
import { useSettingsQuery } from '@/features/profile/hooks/useProfile';
import { PreferencesProvider } from '@/shared/contexts/PreferencesProvider';
import { useSystemColorScheme } from '@/shared/hooks/useSystemColorScheme';
import { DEFAULT_DISPLAY_PREFERENCES } from '@/shared/types/preferences.types';
import { clearCachedTheme, readCachedTheme, writeCachedTheme } from '@/shared/utils/themeCache';
import { conbentThemeConfig } from '@/styles/theme';
import type { DisplayPreferences } from '@/shared/types/preferences.types';

export interface UserPreferencesProviderProps {
  readonly children: ReactNode;
}

/**
 * Binds the signed-in user's saved settings to the presentation layer.
 *
 * This is the one place allowed to know both halves: the profile feature that stores the
 * settings, and the shared context that tables, pagers and formatters read from. Keeping
 * the join here is what lets a shared component honour a preference without importing
 * from a feature.
 *
 * The theme provider lives inside rather than above because the chosen colour mode is
 * itself a preference, and `system` has to be resolved against the device before Gluestack
 * can be told which palette to use.
 *
 * `GluestackUIProvider` must not be rendered under `StrictMode`, or it silently stops
 * applying the colour mode; `App` explains why and is where the boundary is drawn.
 */
export function UserPreferencesProvider({
  children,
}: UserPreferencesProviderProps): React.JSX.Element {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const isRestoringSession = useAppSelector(selectIsRestoringSession);
  const settingsQuery = useSettingsQuery({ enabled: isAuthenticated });
  const systemColorScheme = useSystemColorScheme();

  // Read once on mount. This is a hint for the first paint only, and re-reading it would let
  // the value this component itself writes decide what it falls back to.
  const [cachedTheme] = useState(readCachedTheme);

  const preferences = useMemo<DisplayPreferences>(() => {
    const settings = settingsQuery.data;
    if (settings === undefined) {
      return {
        ...DEFAULT_DISPLAY_PREFERENCES,
        theme: cachedTheme ?? DEFAULT_DISPLAY_PREFERENCES.theme,
      };
    }

    return {
      theme: settings.theme,
      density: settings.density,
      locale: settings.locale,
      timeZone: settings.timeZone,
      pageSize: settings.defaultPageSize,
    };
  }, [settingsQuery.data, cachedTheme]);

  const savedTheme = settingsQuery.data?.theme;
  useEffect(() => {
    if (savedTheme !== undefined) writeCachedTheme(savedTheme);
  }, [savedTheme]);

  // The hint describes whoever was last signed in, so it is dropped once we know nobody is.
  // Waiting for the restore attempt to finish matters: until then there is no session simply
  // because the answer has not arrived, and clearing on that would defeat the whole point.
  useEffect(() => {
    if (!isRestoringSession && !isAuthenticated) clearCachedTheme();
  }, [isRestoringSession, isAuthenticated]);

  const colorMode = preferences.theme === 'system' ? systemColorScheme : preferences.theme;

  return (
    <GluestackUIProvider config={conbentThemeConfig} colorMode={colorMode}>
      <PreferencesProvider value={preferences}>{children}</PreferencesProvider>
    </GluestackUIProvider>
  );
}
